import type { GeneralApiError } from "@/api/custom_types";
import type {
	UseMutationOptions,
	UseMutationResult,
	UseQueryOptions,
	UseQueryResult,
} from "@tanstack/react-query";
import { queryClient } from "./queryClient";

type ArgsFor<T> = [T] extends [] | [undefined] ? [] : [args?: T];

function createReactQueryKeys<T extends Record<string, any>>(
	baseKey: string,
	queryNames: (keyof T)[],
): {
	[K in keyof T]: (...args: ArgsFor<T[K]>) => string[];
} {
	const result = {} as {
		[K in keyof T]: (...args: ArgsFor<T[K]>) => string[];
	};

	for (const name of queryNames) {
		result[name] = ((...args: any[]) => {
			const suffix = args[0] !== undefined ? JSON.stringify(args[0]) : null;
			return suffix ? [baseKey, String(name), suffix] : [baseKey, String(name)];
		}) as (...args: ArgsFor<T[typeof name]>) => string[];
	}

	return result;
}

export type CustomQueryHook<
	TResponse = unknown,
	TParams = unknown,
	TTransformedData = TResponse,
	TError = GeneralApiError,
> = TParams extends NonNullable<TParams>
	? (
			params: TParams,
			options?: Omit<
				UseQueryOptions<TResponse, TError, TTransformedData>,
				"queryKey"
			>,
		) => UseQueryResult<TTransformedData, TError>
	: (
			options?: Omit<
				UseQueryOptions<TResponse, TError, TTransformedData>,
				"queryKey"
			>,
		) => UseQueryResult<TTransformedData, TError>;

export type CustomMutationHook<
	TParams = unknown,
	TResponse = unknown,
	TError = GeneralApiError,
	TContext = unknown,
> = (
	options?: UseMutationOptions<TResponse, TError, TParams, TContext>,
) => UseMutationResult<TResponse, TError, TParams>;

/** Utility function for invalidating queries by key. */
export function invalidateQueries(queryKey: string[]) {
	queryClient.invalidateQueries({ queryKey });
}

export { createReactQueryKeys };
